import { useEffect, useState } from "react";
import { GraduationCap, FileSpreadsheet, BookOpen, AlertCircle, NotebookPen } from "lucide-react";
import { ApiService } from "../../services/api";
import type { NavigatorLessonStatus, JournalEvent } from "../../types/navigator";

const dateFmt = new Intl.DateTimeFormat("tr-TR", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

const fmtDate = (iso: string) => {
  try {
    return dateFmt.format(new Date(iso));
  } catch {
    return "";
  }
};

// Ders başına ilerleme özeti — konu kartlarının kazanım sayılarını toplar.
function LessonProgressCard({ lesson }: { lesson: NavigatorLessonStatus }) {
  const konular = lesson.units.flatMap((u) => u.konular);
  const total = konular.reduce((a, k) => a + (k.kazanimTotal || 0), 0);
  const mastered = konular.reduce((a, k) => a + (k.kazanimMastered || 0), 0);
  const taught = konular.reduce((a, k) => a + (k.kazanimTaught || 0), 0);
  const pct = total ? Math.round((mastered / total) * 100) : 0;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-semibold text-gray-900">{lesson.title}</span>
        </div>
        <span className="text-xs font-semibold text-gray-700">
          {mastered}/{total} pekişti
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
        <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1.5 flex items-center gap-3 text-[11px] text-gray-400">
        <span>%{pct} tamamlandı</span>
        {taught > mastered && <span className="text-amber-600">{taught} kazanım anlatıldı</span>}
      </div>
    </div>
  );
}

export default function Defterim({ lessons }: { lessons: NavigatorLessonStatus[] }) {
  const [events, setEvents] = useState<JournalEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await ApiService.getNavigatorJournal(undefined, 40);
        if (alive) setEvents(r.events || []);
      } catch (e: any) {
        if (alive) setError(e?.message || "Günlük yüklenemedi");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="mx-auto w-full max-w-3xl space-y-5 px-4 py-5 sm:px-6">
      {/* Özet */}
      <div>
        <div className="mb-2 flex items-center gap-2">
          <NotebookPen className="h-5 w-5 text-blue-600" />
          <h2 className="text-base font-semibold text-gray-900">Öğrenme Defterin</h2>
          <span className="text-sm text-gray-400">nerede, ne kadar ilerledin</span>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {lessons.map((lesson) => (
            <LessonProgressCard key={lesson.lessonId} lesson={lesson} />
          ))}
        </div>
      </div>

      {/* Günlük */}
      <div>
        <div className="mb-2 flex items-center gap-2">
          <h2 className="text-base font-semibold text-gray-900">Günlük</h2>
          <span className="text-sm text-gray-400">neler yaptın</span>
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            <AlertCircle className="h-5 w-5 shrink-0" />
            {error}
          </div>
        )}

        {!error && events === null && (
          <div className="grid place-items-center py-10">
            <div className="h-7 w-7 animate-spin rounded-full border-b-2 border-blue-600" />
          </div>
        )}

        {!error && events !== null && events.length === 0 && (
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
            <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-lg bg-blue-50 text-blue-600">
              <NotebookPen className="h-6 w-6" />
            </div>
            <p className="mb-1 text-base font-semibold text-gray-900">Defterin henüz boş</p>
            <p className="mx-auto max-w-sm text-sm text-gray-600">
              "Bana dersi anlat" ile kazanım çalış ya da bir deneme çöz; yaptıkların burada tarihiyle
              birikecek.
            </p>
          </div>
        )}

        {!error && events !== null && events.length > 0 && (
          <ol className="relative space-y-3 border-l border-gray-200 pl-5">
            {events.map((ev, i) => (
              <li key={i} className="relative">
                <span
                  className={`absolute -left-[27px] grid h-6 w-6 place-items-center rounded-full ring-4 ring-white ${
                    ev.type === "taught" ? "bg-blue-100 text-blue-600" : "bg-emerald-100 text-emerald-600"
                  }`}
                >
                  {ev.type === "taught" ? <GraduationCap className="h-3.5 w-3.5" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
                </span>
                <div className="rounded-lg border border-gray-200 bg-white p-3">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-gray-900">{ev.title}</p>
                    <span className="shrink-0 text-[11px] text-gray-400">{fmtDate(ev.at)}</span>
                  </div>
                  {ev.detail && <p className="mt-0.5 text-xs text-gray-500">{ev.detail}</p>}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
